/**
 * ISSUE-1365. The rules behind the composer's game picker, after v1's
 * "Matches" button in `file_select_sheet.dart`: which leagues it offers, how
 * a day reads, how a game row reads, and how many games one share may carry.
 * Pure, so each rule is asserted as data.
 *
 * Each picked game is sent as its own `gameCard` message. The server freezes
 * the card from live data at send time (snapshot rule, ISSUE-175 R6), so the
 * client sends only the event id.
 */

export const GAME_PICKER_TITLE = "Share a game";
export const GAME_PICKER_DESCRIPTION =
  "Pick up to 5 games. Each one is sent as it looks right now.";
export const GAME_PICKER_LEAGUE_LABEL = "League";
export const GAME_PICKER_EMPTY = "No games on this day.";
export const GAME_PICKER_FAILED = "We could not load the games. Try again.";
export const GAME_PICKER_NO_LEAGUES = "No leagues to pick from yet.";
export const GAME_PICKER_PREVIOUS_DAY = "Previous day";
export const GAME_PICKER_NEXT_DAY = "Next day";
export const COMPOSER_GAME_ACTION_LABEL = "Share a game";

/** One share sends at most this many cards. */
export const GAME_PICKER_MAX = 5;

export function gamePickerSendLabel(count: number): string {
  if (count <= 1) return "Send game";
  return `Send ${count} games`;
}

export type typeGamePickerLeague = { leagueId: string; label: string };

/**
 * Live leagues only, the fan's followed leagues first (in their own order),
 * then the rest in catalog order. Hidden and coming-soon leagues have no
 * games to share.
 */
export function buildGamePickerLeagues(input: {
  sports: readonly {
    leagues: readonly {
      leagueId: string;
      name: string;
      shortName: string;
      visibilityState: "hidden" | "comingSoon" | "live";
    }[];
  }[];
  followedLeagueIds: readonly string[];
}): typeGamePickerLeague[] {
  const live = input.sports
    .flatMap((sport) => sport.leagues)
    .filter((league) => league.visibilityState === "live")
    .map((league) => ({
      leagueId: league.leagueId,
      label: league.shortName.trim() !== "" ? league.shortName : league.name,
    }));
  const byId = new Map(live.map((league) => [league.leagueId, league]));
  const followed = input.followedLeagueIds
    .map((leagueId) => byId.get(leagueId))
    .filter((league): league is typeGamePickerLeague => league !== undefined);
  const followedIds = new Set(followed.map((league) => league.leagueId));
  return [
    ...followed,
    ...live.filter((league) => !followedIds.has(league.leagueId)),
  ];
}

/** `Today`, `Yesterday`, `Tomorrow`, or `Sat, Nov 8` for any other day. */
export function gamePickerDayLabel(day: string, today: string): string {
  const dayMs = Date.parse(`${day}T00:00:00Z`);
  const todayMs = Date.parse(`${today}T00:00:00Z`);
  const delta = Math.round((dayMs - todayMs) / 86_400_000);
  if (delta === 0) return "Today";
  if (delta === -1) return "Yesterday";
  if (delta === 1) return "Tomorrow";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(new Date(dayMs));
}

type typeTeamInput = {
  name: string;
  shortName?: string | null;
  score?: number | null;
};

/** "Bills 21, Jets 17" once there is a score, else "Bills at Jets". */
export function gamePickerMatchup(input: {
  away: typeTeamInput;
  home: typeTeamInput;
}): string {
  const name = (team: typeTeamInput): string =>
    team.shortName !== undefined &&
    team.shortName !== null &&
    team.shortName.trim() !== ""
      ? team.shortName
      : team.name;
  const awayScore = input.away.score ?? null;
  const homeScore = input.home.score ?? null;
  if (awayScore !== null && homeScore !== null) {
    return `${name(input.away)} ${awayScore}, ${name(input.home)} ${homeScore}`;
  }
  return `${name(input.away)} at ${name(input.home)}`;
}

/**
 * Adds or removes one game. Adding past the cap is a no-op, so the fan's
 * earlier picks are never dropped to make room.
 */
export function toggleGamePick(
  picked: readonly string[],
  eventId: string,
): string[] {
  if (picked.includes(eventId)) return picked.filter((id) => id !== eventId);
  if (picked.length >= GAME_PICKER_MAX) return [...picked];
  return [...picked, eventId];
}
